import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Megaphone, Users, GraduationCap, PoundSterling, TrendingUp, Plus, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import KanbanBoard, { StatStrip, CardTag, formatGBP, timeAgo } from '@/components/training/KanbanBoard';
import LeadDetailDrawer from '@/components/training/LeadDetailDrawer';
import AdSpendImport from '@/components/training/AdSpendImport';
import { usePipelineMove, MoveToSelect, STAGE_TITLE } from '@/components/training/PipelineMove';

const PERIODS = [
  { value: 'month', label: 'Enquired this month' },
  { value: '30', label: 'Enquired last 30 days' },
  { value: '90', label: 'Enquired last 90 days' },
  { value: '365', label: 'Enquired last 12 months' },
  { value: 'all', label: 'All students' },
];

const CHANNELS = [
  { value: 'meta', label: 'Meta (Facebook / Instagram)' },
  { value: 'google', label: 'Google' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'other', label: 'Other' },
];
const CHANNEL_LABEL = { meta: 'Meta', google: 'Google', tiktok: 'TikTok', other: 'Other' };

const SOURCE_LABEL = { own: 'Own lead', platform: 'Sarum ads', referral: 'Referral', manual: 'Own lead' };
const AD_LABEL = { meta: 'Meta ad', google: 'Google ad', tiktok: 'TikTok ad', microsoft: 'Microsoft ad' };

const today = () => new Date().toISOString().slice(0, 10);


const StudentPipelinePage = () => {
  const { user, selectedBusiness } = useAuth();
  const canEdit = ['admin', 'super_admin'].includes(user?.role);
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spendOpen, setSpendOpen] = useState(false);
  const [spendEntries, setSpendEntries] = useState([]);
  const [spendForm, setSpendForm] = useState({ channel: 'meta', amount: '', date: today(), note: '' });
  const [savingSpend, setSavingSpend] = useState(false);
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const { requestMove, moveDialog } = usePipelineMove(() => fetchPipeline());

  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training/pipeline?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
      });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load pipeline');
    } catch {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, [period, token, selectedBusiness?.business_id]);

  const fetchSpend = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/ad-spend`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
      });
      if (res.ok) setSpendEntries(await res.json());
    } catch { /* list is optional */ }
  }, [token]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const openSpend = () => { fetchSpend(); setSpendOpen(true); };

  const addSpend = async () => {
    const amount = parseFloat(spendForm.amount);
    if (!amount || amount <= 0) { toast.error('Enter how much was spent'); return; }
    setSavingSpend(true);
    try {
      const res = await fetch(`${API_URL}/api/training/ad-spend`, {
        method: 'POST', headers: authHeaders, credentials: 'include',
        body: JSON.stringify({ ...spendForm, amount }),
      });
      if (res.ok) {
        toast.success('Ad spend added');
        setSpendForm({ channel: spendForm.channel, amount: '', date: today(), note: '' });
        fetchSpend();
        fetchPipeline();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add spend');
      }
    } finally {
      setSavingSpend(false);
    }
  };

  const deleteSpend = async (spendId) => {
    const res = await fetch(`${API_URL}/api/training/ad-spend/${spendId}`, {
      method: 'DELETE', headers: authHeaders, credentials: 'include',
    });
    if (res.ok) { fetchSpend(); fetchPipeline(); }
    else toast.error('Failed to delete');
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
      </div>
    );
  }

  const s = data?.summary || {};
  const stages = data?.stages || {};
  const cols = data?.columns || {};
  const spendSub = Object.entries(s.spend_by_channel || {}).map(([k, v]) => `${CHANNEL_LABEL[k] || k} ${formatGBP(v)}`).join(' · ');

  const personCard = (fromKey) => (card) => (
    <div>
      <button type="button" className="w-full text-left" onClick={() => setDrawerLeadId(card.lead_id)}>
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-slate-900 truncate">{card.name}</p>
          <span className={`text-xs font-semibold whitespace-nowrap ${card.value_type === 'actual' ? 'text-green-700' : 'text-slate-500'}`}>
            {fromKey === 'drop_offs' || !card.value ? '' : formatGBP(card.value)}
          </span>
        </div>
        {card.course && <p className="text-xs text-slate-500 mt-0.5 truncate">{card.course}</p>}
        <div className="flex flex-wrap items-center gap-1 mt-2">
          {card.reason && <CardTag tone="red">{card.reason}</CardTag>}
          {fromKey === 'leads' && card.qualified && <CardTag tone="violet">Qualified</CardTag>}
          {card.ad_channel && <CardTag tone="blue">{AD_LABEL[card.ad_channel] || card.ad_channel}</CardTag>}
          {card.deposit && <CardTag tone="green">Deposit{card.deposit !== true ? ` £${Number(card.deposit).toLocaleString('en-GB')}` : ''}</CardTag>}
          {card.registration_date && <CardTag tone="blue">Reg {new Date(`${card.registration_date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</CardTag>}
          {card.source && <CardTag>{SOURCE_LABEL[card.source] || card.source}</CardTag>}
        </div>
      </button>
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <span className="text-[11px] text-slate-400">{card.date ? timeAgo(card.date) : ''}</span>
        {canEdit && (
          <MoveToSelect card={card} from={fromKey} onMove={requestMove} />
        )}
      </div>
    </div>
  );

  const column = (key, title, tone, valueLabel, emptyText) => ({
    key, title, tone, emptyText,
    count: stages[key]?.count || 0,
    subtitle: key === 'drop_offs' || !stages[key]?.value ? undefined : `${formatGBP(stages[key].value)} ${valueLabel}`,
    cards: cols[key] || [],
    renderCard: personCard(key),
  });

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pipeline</h1>
          <p className="text-slate-500 mt-1">Every student from first enquiry to finished course, and what each stage is worth.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-52 rounded-xl h-9" aria-label="Which students"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="rounded-xl h-9" onClick={fetchPipeline} aria-label="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button className="btn-primary rounded-xl h-9" onClick={openSpend}>
            <Megaphone className="w-4 h-4 mr-1" /> Ad spend
          </Button>
        </div>
      </div>

      <StatStrip items={[
        { label: 'Ad spend', icon: Megaphone, value: formatGBP(s.ad_spend), sub: spendSub || (canEdit ? 'Add your spend to see cost per student' : 'No spend added') },
        { label: 'Leads', icon: Users, value: s.total_leads ?? 0, sub: [s.qualified_leads != null ? `${s.qualified_leads} qualified` : null, s.cost_per_qualified_lead != null ? `${formatGBP(s.cost_per_qualified_lead)} per qualified` : (s.cost_per_lead != null ? `${formatGBP(s.cost_per_lead)} per lead` : null)].filter(Boolean).join(' · ') || null },
        { label: 'Students enrolled', icon: GraduationCap, value: s.students ?? 0, sub: s.cost_per_student != null ? `${formatGBP(s.cost_per_student)} per student` : (s.registration_to_enrolment_rate != null ? `${s.registration_to_enrolment_rate}% of registrations enrol` : null) },
        { label: 'Course sales', icon: PoundSterling, value: formatGBP(s.revenue), sub: s.roas != null ? `${s.roas}x return on ad spend` : null },
        (s.profit || 0) > 0
          ? { label: 'Profit', icon: TrendingUp, value: formatGBP(s.profit), sub: 'after ad spend and delivery cost' }
          : { label: 'Still in play', icon: TrendingUp, value: formatGBP((stages.leads?.value || 0) + (stages.registrations?.value || 0)), sub: 'leads and registered students yet to enrol' },
      ]} />

      {s.courses_missing_price > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>
            {s.courses_missing_price} course{s.courses_missing_price > 1 ? 's have' : ' has'} no price, so some £ values are missing.{' '}
            <Link to="/courses" className="font-semibold underline">Add prices on the Courses page</Link>.
          </p>
        </div>
      )}

      <KanbanBoard onMove={canEdit ? requestMove : undefined} columns={[
        column('leads', STAGE_TITLE.leads, 'slate', 'potential', 'No new leads'),
        column('registrations', STAGE_TITLE.registrations, 'blue', 'potential', 'No one booked in'),
        column('enrolled', STAGE_TITLE.enrolled, 'pink', 'sold', 'No one enrolled yet'),
        column('completed', STAGE_TITLE.completed, 'green', 'sold', 'No one finished yet'),
        column('drop_offs', STAGE_TITLE.drop_offs, 'red', 'lost', 'No one dropped out'),
      ]} />

      {data?.breakdown && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {[
            { key: 'offer', title: 'By offer' },
            { key: 'location', title: 'By location' },
            { key: 'source', title: 'By lead source' },
          ].map(({ key, title }) => (
            <div key={key} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <p className="px-4 py-2.5 text-sm font-semibold text-slate-900 border-b border-slate-100">{title}</p>
              {(data.breakdown[key] || []).length === 0 ? (
                <p className="px-4 py-4 text-xs text-slate-400">No data yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500">
                        <th className="text-left font-medium px-4 py-2"> </th>
                        <th className="text-right font-medium px-2 py-2">Leads</th>
                        <th className="text-right font-medium px-2 py-2">Students</th>
                        <th className="text-right font-medium px-2 py-2">Conv.</th>
                        <th className="text-right font-medium px-4 py-2">Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.breakdown[key].map(row => (
                        <tr key={row.label} className="border-t border-slate-50">
                          <td className="px-4 py-2 text-slate-800 truncate max-w-[9rem]">{row.label}</td>
                          <td className="px-2 py-2 text-right text-slate-600">{row.leads}</td>
                          <td className="px-2 py-2 text-right text-slate-600">{row.students}</td>
                          <td className="px-2 py-2 text-right text-slate-600">{row.leads ? `${Math.round((row.students / row.leads) * 100)}%` : '—'}</td>
                          <td className="px-4 py-2 text-right font-semibold text-slate-900">{formatGBP(row.sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-slate-400">
        {canEdit ? 'Drag a card to another stage (or use "Move to…") — you\'ll be asked to confirm. ' : ''}
        Enrolled and completed show the actual price (after referral discounts). Leads and registrations show the price GHL sent for them, or the matching course price.
      </p>

      <Dialog open={spendOpen} onOpenChange={setSpendOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Ad spend</DialogTitle>
          </DialogHeader>
          {canEdit ? (
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Channel</label>
                  <Select value={spendForm.channel} onValueChange={(v) => setSpendForm(p => ({ ...p, channel: v }))}>
                    <SelectTrigger className="rounded-xl h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>{CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Amount (£)</label>
                  <Input type="number" min="0" step="0.01" inputMode="decimal" value={spendForm.amount} onChange={(e) => setSpendForm(p => ({ ...p, amount: e.target.value }))} placeholder="500" className="rounded-xl h-10" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Date</label>
                  <Input type="date" value={spendForm.date} onChange={(e) => setSpendForm(p => ({ ...p, date: e.target.value }))} className="rounded-xl h-10" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Note</label>
                  <Input value={spendForm.note} onChange={(e) => setSpendForm(p => ({ ...p, note: e.target.value }))} placeholder="e.g. Fast Track campaign" className="rounded-xl h-10" />
                </div>
              </div>
              <Button className="btn-primary rounded-xl w-full" onClick={addSpend} disabled={savingSpend}>
                {savingSpend ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add spend
              </Button>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Only admins can add ad spend.</p>
          )}
          <AdSpendImport canEdit={canEdit} onChanged={() => { fetchSpend(); fetchPipeline(); }} />
          <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 border-t border-slate-100 mt-2">
            {spendEntries.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No spend added yet</p>
            ) : spendEntries.map(e => (
              <div key={e.spend_id} className="flex items-center justify-between gap-2 py-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">{formatGBP(e.amount)} · {CHANNEL_LABEL[e.channel] || e.channel}</p>
                  <p className="text-xs text-slate-400 truncate">{new Date(`${e.date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}{e.source === 'meta_sync' ? ' · Synced from Meta' : e.source === 'csv' ? ' · Imported' : e.note ? ` · ${e.note}` : ''}</p>
                </div>
                {canEdit && (
                  <Button variant="ghost" size="sm" aria-label="Delete spend entry" onClick={() => deleteSpend(e.spend_id)}>
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setSpendOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeadDetailDrawer
        open={!!drawerLeadId}
        onOpenChange={(open) => { if (!open) setDrawerLeadId(null); }}
        leadId={drawerLeadId}
        onLeadUpdated={fetchPipeline}
      />
    </div>
  );
};

export default StudentPipelinePage;
